import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  DailyCountItem,
  DailyCountItemStatus,
  DailyCountList,
  defaultImage,
  onHandOf,
  StoreProduct,
} from '@bofrak-backend/shared';
import {
  InventoryBreakdown,
  QuickInventoryCount,
} from '@bofrak-backend/shared-ui';
import { createApiAdapter } from '../api/backend';
import { dailyCountDb } from '../db/daily-count-db';
import { useMutation } from 'react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRecoilValue } from 'recoil';
import {
  adminAtom,
  merchantAtom,
  selectedStoreAtom,
  storeProductsAtom,
  accessTokenAtom,
} from '../recoil/atoms';

/* ─── DailyCountItemRow ─────────────────────────────────────── */

const DailyCountItemRow: React.FC<{
  item: DailyCountItem;
  product?: StoreProduct;
  onCount?: () => void;
  cardBg: string;
  borderColor: string;
}> = ({ item, product, onCount, cardBg, borderColor }) => {
  const isCounted = item.status === DailyCountItemStatus.COUNTED;
  const isCarriedOver = item.status === DailyCountItemStatus.CARRIED_OVER;
  const isMismatched =
    isCounted && (item.is_short || item.counted_stock !== item.expected_stock);

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={isMismatched ? 'red.300' : borderColor}
      size="sm"
    >
      <CardBody py={3} px={4}>
        <Flex justify="space-between" align="center" gap={3}>
          <Image
            src={product?.image || defaultImage}
            alt={item.product_name}
            boxSize="40px"
            borderRadius="md"
            objectFit="contain"
            flexShrink={0}
            fallbackSrc={defaultImage}
          />
          <Box flex={1} minW={0}>
            <HStack spacing={2} mb={1}>
              <Text fontWeight="medium" fontSize="sm" noOfLines={1} flex={1} minW={0}>
                {item.product_name}
              </Text>
              {isCarriedOver && (
                <Badge colorScheme="purple" fontSize="2xs" flexShrink={0}>
                  Carried Over
                </Badge>
              )}
            </HStack>
            {isCounted ? (
              <HStack spacing={3} fontSize="xs" color="gray.500">
                <Text>
                  Expected:{' '}
                  {product ? (
                    <InventoryBreakdown
                      inventory={item.expected_stock ?? 0}
                      fractions={[product, ...product.store_product_fractions]}
                      label="Expected"
                    />
                  ) : (
                    item.expected_stock ?? '-'
                  )}
                </Text>
                <Text>
                  Counted:{' '}
                  {product ? (
                    <InventoryBreakdown
                      inventory={item.counted_stock ?? 0}
                      fractions={[product, ...product.store_product_fractions]}
                      label="Counted"
                    />
                  ) : (
                    item.counted_stock ?? '-'
                  )}
                </Text>
              </HStack>
            ) : (
              <Text fontSize="xs" color="gray.500">
                Stock:{' '}
                {product ? (
                  <InventoryBreakdown
                    inventory={product.inventory}
                    fractions={[product, ...product.store_product_fractions]}
                    label="Stock"
                  />
                ) : (
                  '—'
                )}
              </Text>
            )}
          </Box>
          {onCount && (
            <Button
              size="xs"
              colorScheme={isCounted ? 'gray' : 'blue'}
              variant={isCounted ? 'outline' : 'solid'}
              onClick={onCount}
              flexShrink={0}
            >
              {isCounted ? 'Recount' : 'Count'}
            </Button>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
};

/* ─── DailyCountTab ─────────────────────────────────────────── */

const DailyCountTab: React.FC = () => {
  const merchant = useRecoilValue(merchantAtom);
  const selectedStore = useRecoilValue(selectedStoreAtom);
  const admin = useRecoilValue(adminAtom);
  const products = useRecoilValue(storeProductsAtom);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedBg = useColorModeValue('gray.50', 'gray.900');
  const textMuted = useColorModeValue('gray.500', 'gray.400');

  const todayDate = new Date().toISOString().split('T')[0];
  const toast = useToast();
  const accessToken = useRecoilValue(accessTokenAtom);

  // Authenticated adapter for daily-count calls (rebuilt when the token arrives).
  const apiAdapter = useMemo(
    () => createApiAdapter(accessToken ?? ''),
    [accessToken],
  );

  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);
  const [selectedItemExpectedStock, setSelectedItemExpectedStock] = useState(0);
  const [recountPreviousStock, setRecountPreviousStock] = useState<number | null>(null);
  const [recountReason, setRecountReason] = useState('');
  const [reasonResolve, setReasonResolve] = useState<(() => void) | null>(null);
  const [lastCountedStock, setLastCountedStock] = useState<number>(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isReasonModalOpen,
    onOpen: onOpenReasonModal,
    onClose: onCloseReasonModal,
  } = useDisclosure();

  const productMap = useMemo(() => {
    const map = new Map<string, StoreProduct>();
    for (const p of products) {
      map.set(p.id, p);
    }
    return map;
  }, [products]);

  // ── Fetch today's list (IndexedDB cache-first, network refresh in background) ──
  // undefined = still reading cache / network in flight; null = no list.
  const cachedList = useLiveQuery(
    () => dailyCountDb.getList(selectedStore?.id ?? '', todayDate),
    [selectedStore?.id, todayDate],
  );
  const [serverList, setServerList] = useState<
    DailyCountList | null | undefined
  >(undefined);
  const [isFetchingList, setIsFetchingList] = useState(false);

  const persistList = useCallback((list: DailyCountList) => {
    setServerList(list);
    void dailyCountDb.putList(list);
  }, []);

  const fetchTodayList = useCallback(async (): Promise<
    DailyCountList | null | undefined
  > => {
    if (!merchant?.id || !selectedStore?.id) return undefined;
    setIsFetchingList(true);
    try {
      const page = await apiAdapter.getDailyCountLists({
        merchant_id: merchant.id,
        store_id: selectedStore.id,
        date: todayDate,
      });
      const fresh = (page.daily_count_lists?.[0] ?? null) as DailyCountList | null;
      setServerList(fresh);
      if (fresh) void dailyCountDb.putList(fresh);
      return fresh;
    } catch (e) {
      // Network problems are expected on shop-floor connections — keep the
      // cached list on screen and let the caller retry on next conditions.
      console.warn('[DailyCountTab] fetch failed, using cache:', e);
      return undefined;
    } finally {
      setIsFetchingList(false);
    }
  }, [apiAdapter, merchant?.id, selectedStore?.id, todayDate]);

  // ── Auto-trigger ──
  const { mutate: triggerDailyCount } = useMutation(
    'trigger-daily-count-lite',
    () => apiAdapter.triggerDailyCountList(merchant!.id, { store_id: selectedStore!.id }),
    {
      onSuccess: (data: DailyCountList) => persistList(data),
      onError: () => {},
    },
  );

  useEffect(() => {
    if (!merchant?.id || !selectedStore?.id) return;
    let cancelled = false;
    (async () => {
      const fresh = await fetchTodayList();
      if (cancelled || fresh === undefined) return;
      if (fresh === null) triggerDailyCount();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchTodayList, triggerDailyCount, merchant?.id, selectedStore?.id]);

  // Cache-first merge: the server result wins once available; otherwise the
  // IndexedDB copy renders immediately so users never wait on slow networks.
  const dailyCountData = serverList !== undefined ? serverList : cachedList;
  const isDailyCountLoading =
    dailyCountData === undefined ||
    (dailyCountData === null && isFetchingList);

  // ── Record count ──
  const { mutateAsync: recordDailyCount } = useMutation(
    'record-daily-count-lite',
    (params: { productId: string; expectedStock: number; countedStock: number; recountReason?: string }) =>
      apiAdapter.recordDailyCount(merchant!.id, {
        store_id: selectedStore!.id,
        count_date: todayDate,
        product_id: params.productId,
        counted_by: admin!.id,
        counted_stock: params.countedStock,
        expected_stock: params.expectedStock,
        recount_reason: params.recountReason,
      }),
    {
      onSuccess: (data: DailyCountList) => {
        persistList(data);
        toast({ title: 'Count recorded', status: 'success', duration: 2000 });
      },
      onError: () => { toast({ title: 'Failed to record count', status: 'error', duration: 3000 }); },
    },
  );

  // ── Categorize ──
  const { uncounted, mismatched, correct } = useMemo(() => {
    if (!dailyCountData?.items) return { uncounted: [], mismatched: [], correct: [] };
    const u: DailyCountItem[] = [], m: DailyCountItem[] = [], c: DailyCountItem[] = [];
    for (const item of dailyCountData.items) {
      if (item.status === DailyCountItemStatus.PENDING || item.status === DailyCountItemStatus.CARRIED_OVER) {
        u.push(item);
      } else if (item.status === DailyCountItemStatus.COUNTED) {
        (item.is_short || item.counted_stock !== item.expected_stock ? m : c).push(item);
      }
    }
    return { uncounted: u, mismatched: m, correct: c };
  }, [dailyCountData]);

  const totalItems = dailyCountData?.items?.length ?? 0;
  const countedItems = mismatched.length + correct.length;
  const completionRate = totalItems > 0 ? (countedItems / totalItems) * 100 : 0;

  // ── Handlers ──
  const handleCountItem = useCallback((item: DailyCountItem) => {
    const product = productMap.get(item.product_id);
    if (!product) return;
    setSelectedProduct(product);
    // A count finds everything on the shelf, held stock included.
    setSelectedItemExpectedStock(onHandOf(product));
    setRecountPreviousStock(
      item.status === DailyCountItemStatus.COUNTED && item.counted_stock !== null
        ? item.counted_stock : null,
    );
    onOpen();
  }, [productMap, onOpen]);

  const handleBeforeSubmit = useCallback(async (actualStock: number) => {
    setLastCountedStock(actualStock);
    if (recountPreviousStock !== null && actualStock < recountPreviousStock) {
      setRecountReason('');
      return new Promise<void>((resolve) => {
        setReasonResolve(() => resolve);
        onOpenReasonModal();
      });
    }
  }, [recountPreviousStock, onOpenReasonModal]);

  const handleSuccess = useCallback((productId: string) => {
    recordDailyCount({
      productId,
      expectedStock: selectedItemExpectedStock,
      countedStock: lastCountedStock,
      recountReason: recountReason.trim() || undefined,
    });
    setRecountReason('');
    setRecountPreviousStock(null);
  }, [selectedItemExpectedStock, recordDailyCount, recountReason, lastCountedStock]);

  const handleModalClose = () => { setSelectedProduct(null); onClose(); };
  const handleReasonDismiss = () => { reasonResolve?.(); setReasonResolve(null); setRecountReason(''); onCloseReasonModal(); };
  const handleReasonSubmit = () => { reasonResolve?.(); setReasonResolve(null); onCloseReasonModal(); };

  if (isDailyCountLoading) {
    return <Center py={20}><VStack spacing={4}><Spinner size="lg" color="teal.500" /><Text color="gray.500">Loading daily count list...</Text></VStack></Center>;
  }
  if (!dailyCountData) {
    return <Center py={20}><VStack spacing={4}><Text fontSize="lg" color={textMuted}>No daily count list for today.</Text><Text fontSize="sm" color={textMuted}>Daily counts may not be enabled for this store.</Text></VStack></Center>;
  }

  return (
    <Box px={{ base: 2, md: 4 }} py={4}>
      {/* Today's Progress */}
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} mb={4}>
        <CardBody>
          <VStack spacing={3} align="stretch">
            <Flex justify="space-between" align="center">
              <Heading size="sm">Today's Progress</Heading>
              <Badge colorScheme={completionRate === 100 ? 'green' : 'orange'} fontSize="sm" px={2} py={1} borderRadius="md">
                {countedItems}/{totalItems} counted
              </Badge>
            </Flex>
            <Progress value={completionRate} colorScheme={completionRate === 100 ? 'green' : 'teal'} borderRadius="full" size="sm" />
            <HStack spacing={4} fontSize="xs" color={textMuted}>
              <Text>{uncounted.length} uncounted</Text>
              <Text>{mismatched.length} mismatched</Text>
              <Text>{correct.length} correct</Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Uncounted */}
      {uncounted.length > 0 && (
        <Box mb={4}>
          <HStack mb={2}><Badge colorScheme="orange" fontSize="xs">Uncounted</Badge><Text fontSize="sm" color={textMuted}>{uncounted.length} item{uncounted.length !== 1 ? 's' : ''}</Text></HStack>
          <VStack spacing={2} align="stretch">
            {uncounted.map(item => (
              <DailyCountItemRow key={item.product_id} item={item} product={productMap.get(item.product_id)} onCount={() => handleCountItem(item)} cardBg={cardBg} borderColor={borderColor} />
            ))}
          </VStack>
        </Box>
      )}

      {/* Mismatched */}
      {mismatched.length > 0 && (
        <Box mb={4}>
          <HStack mb={2}><Badge colorScheme="red" fontSize="xs">Mismatched</Badge><Text fontSize="sm" color={textMuted}>{mismatched.length} item{mismatched.length !== 1 ? 's' : ''}</Text></HStack>
          <VStack spacing={2} align="stretch">
            {mismatched.map(item => (
              <DailyCountItemRow key={item.product_id} item={item} product={productMap.get(item.product_id)} onCount={() => handleCountItem(item)} cardBg={cardBg} borderColor={borderColor} />
            ))}
          </VStack>
        </Box>
      )}

      {/* Correct */}
      {correct.length > 0 && (
        <Box mb={4}>
          <HStack mb={2}><Badge colorScheme="green" fontSize="xs">Correct</Badge><Text fontSize="sm" color={textMuted}>{correct.length} item{correct.length !== 1 ? 's' : ''}</Text></HStack>
          <VStack spacing={2} align="stretch">
            {correct.map(item => (
              <DailyCountItemRow key={item.product_id} item={item} product={productMap.get(item.product_id)} onCount={() => handleCountItem(item)} cardBg={mutedBg} borderColor={borderColor} />
            ))}
          </VStack>
        </Box>
      )}

      {uncounted.length === 0 && totalItems > 0 && (
        <Center py={8}><VStack spacing={2}><Icon as={CheckCircleIcon} boxSize={10} color="green.500" /><Text fontWeight="bold" color="green.600">All items counted!</Text></VStack></Center>
      )}

      {/* Count Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size={{ base: 'full', sm: 'md', md: 'lg' }} isCentered>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, sm: 4 }}>
          <ModalCloseButton />
          <ModalBody py={6}>
            {selectedProduct && merchant && selectedStore && admin && (
              <QuickInventoryCount
                item={selectedProduct}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
                merchant={merchant}
                store={selectedStore}
                source={{ id: admin.id, name: admin.name }}
                onBeforeSubmit={handleBeforeSubmit}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Reason Modal */}
      <Modal isOpen={isReasonModalOpen} onClose={handleReasonDismiss} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader fontSize="md">Inventory Decreased</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>The recounted stock is lower than the previous count. You may optionally provide a reason.</Text>
            <Textarea placeholder="Why did inventory decrease? (optional)" value={recountReason} onChange={e => setRecountReason(e.target.value)} size="sm" rows={3} />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" onClick={handleReasonDismiss} mr={2}>Skip</Button>
            <Button size="sm" colorScheme="teal" onClick={handleReasonSubmit}>Submit</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DailyCountTab;
